/**
 * @aiPurpose shared catalog of system role names eligible for the
 *   self-shift toggle (`PATCH /api/users/me/shift`). Both backend
 *   `UsersShiftService` and web `OnShiftToggle` consume this so the
 *   two surfaces never diverge — adding a new shift role is a single
 *   edit here.
 * @aiInvariant entries are `Role.name` machine identifiers and must be
 *   `isSystem = true` at the call site. Legacy alias `'logist'` is
 *   intentionally NOT listed: by the time `logist_ru` shipped, every
 *   environment had run the `20260430140000_rename_logist_to_logist_cn`
 *   migration, and shift gating on a snapshot still carrying the legacy
 *   name is acceptable behaviour. Order matters only for stable
 *   enumerations; consumers use the `Set`-based predicate below.
 * @aiSecurity load-bearing for the per-role last-on-shift invariant —
 *   `countEligibleLogistsByRole` reads this list to decide which role
 *   buckets to count independently. Reordering or adding aliases here
 *   without paired backend/web edits breaks the cross-surface
 *   contract.
 */
export const LOGIST_SHIFT_ROLE_NAMES = ['logist_cn', 'logist_ru'] as const;
export type LogistShiftRoleName = (typeof LOGIST_SHIFT_ROLE_NAMES)[number];

const SHIFT_ROLE_SET: ReadonlySet<string> = new Set(LOGIST_SHIFT_ROLE_NAMES);

/**
 * @aiPurpose decide whether a `Role.name` machine identifier belongs to
 *   the self-shift toggle eligibility set. Used by the backend
 *   self-shift endpoint, the admin role-change/deactivate guards, the
 *   `/auth/me` last-on-shift indicator, and the web shift-toggle gate.
 * @aiInvariant pure predicate — does not validate `isSystem`; callers
 *   must apply that check separately because they already hold the
 *   `Role` row context. Returns `false` for `null` / `undefined` /
 *   empty / unknown names and for legacy aliases such as `'logist'`.
 */
export function isShiftRoleName(roleName: string | null | undefined): boolean {
  if (!roleName) return false;
  return SHIFT_ROLE_SET.has(roleName);
}
